import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import type { Contact, RequestStatus } from '@indutrust/shared-model';
import { ModalComponent } from '@indutrust/shared-ui';
import { provideTranslocoScopeSharedI18n } from '@indutrust/shared-util';
import { TranslocoDirective } from '@jsverse/transloco';
import { ButtonDirective } from 'primeng/button';
import { ContactFormComponent } from './contact-form.component';

/**
 * Modal wrapper for {@link ContactFormComponent}.
 *
 * Renders the form inside {@link ModalComponent} with a cancel + create footer.
 * Emits {@link save} when the form submits and {@link closed} on cancel or
 * backdrop close.
 *
 * @example
 * <idt-contact-modal
 *   [loading]="pending()"
 *   [requestStatus]="status()"
 *   (save)="onCreate($event)"
 *   (closed)="showModal.set(false)" />
 */
@Component({
  selector: 'idt-contact-modal',
  imports: [
    ButtonDirective,
    ContactFormComponent,
    ModalComponent,
    TranslocoDirective,
  ],
  template: `
    <idt-modal
      *transloco="let t; prefix: 'sharedContact.modal'"
      icon="user"
      [subtitle]="editMode() ? t('description_edit') : t('description')"
      [title]="editMode() ? t('title_edit') : t('title')"
      (closed)="closed.emit()">
      <idt-contact-form
        #contactFormRef
        [contact]="contact()"
        [requestStatus]="requestStatus()"
        (submitted)="save.emit($event)"
        (validChange)="formValid.set($event)" />

      <div class="contents" modalFooter>
        <button
          pButton
          severity="secondary"
          type="button"
          (click)="closed.emit()">
          {{ t('buttons.cancel') }}
        </button>
        <button
          pButton
          type="submit"
          [disabled]="!formValid()"
          [loading]="loading()"
          (click)="contactFormComponent().submitForm()">
          <i aria-hidden="true" class="pi pi-save"></i>
          {{ editMode() ? t('buttons.save') : t('buttons.create') }}
        </button>
      </div>
    </idt-modal>
  `,
  styles: ``,
  providers: [provideTranslocoScopeSharedI18n()],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactModalComponent {
  readonly contact = input<Contact>();
  readonly loading = input(false);
  readonly requestStatus = input<RequestStatus>();

  readonly closed = output();
  readonly save = output<Omit<Contact, 'id'>>();

  readonly contactFormComponent = viewChild.required(ContactFormComponent);

  readonly formValid = signal(false);

  readonly editMode = computed(() => !!this.contact());
}
